import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Real-time chat notification hook.
 *
 * For USERS  → tracks unread admin messages across all their open sessions.
 * For ADMINS → tracks unread user messages across ALL open sessions.
 *
 * Subscribes immediately on mount (no click required) so both sides
 * always know when a new message arrives.
 */

// Shared notification sound (lazy-loaded once)
let _audioCtx = null;
function playNotificationSound() {
  try {
    if (!_audioCtx) _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _audioCtx;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1046.5, ctx.currentTime + 0.08);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch {
    // AudioContext not available – silently ignore
  }
}

// Flash browser tab title
let _flashInterval = null;
function flashTitle(message) {
  if (_flashInterval) return; // already flashing
  const original = document.title;
  let show = true;
  _flashInterval = setInterval(() => {
    document.title = show ? message : original;
    show = !show;
  }, 1200);
  // Stop flashing on window focus
  const stop = () => {
    clearInterval(_flashInterval);
    _flashInterval = null;
    document.title = original;
    window.removeEventListener('focus', stop);
  };
  window.addEventListener('focus', stop);
  // Auto-stop after 30 seconds
  setTimeout(stop, 30000);
}

export function useUserChatNotifications(userId) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [latestMessage, setLatestMessage] = useState(null);
  const mountedRef = useRef(true);

  // Fetch initial unread count
  const fetchUnread = useCallback(async () => {
    if (!userId) return;
    try {
      // Get all open sessions for this user
      const { data: sessions } = await supabase
        .from('chat_sessions')
        .select('id')
        .eq('user_id', userId)
        .eq('status', 'open');

      if (!sessions?.length) { setUnreadCount(0); return; }

      const sessionIds = sessions.map(s => s.id);

      // Count unread admin messages NOT sent by the user
      const { count } = await supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .in('session_id', sessionIds)
        .neq('sender_role', 'user')
        .eq('read_by_user', false);

      if (mountedRef.current) setUnreadCount(count || 0);
    } catch (err) {
      console.warn('Failed to fetch user chat unread count:', err);
    }
  }, [userId]);

  useEffect(() => { fetchUnread(); }, [fetchUnread]);

  // Real-time subscription – listen for ANY new message in user's sessions
  useEffect(() => {
    if (!userId) return;

    // Subscribe to new chat messages globally, then filter client-side
    const channel = supabase
      .channel(`user-chat-notify-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
        },
        async (payload) => {
          const msg = payload.new;
          // Only care about admin/system messages
          if (msg.sender_role === 'user') return;

          // Check if this message belongs to one of the user's sessions
          const { data: session } = await supabase
            .from('chat_sessions')
            .select('id')
            .eq('id', msg.session_id)
            .eq('user_id', userId)
            .maybeSingle();

          if (!session) return; // Not our session

          if (mountedRef.current) {
            setUnreadCount(prev => prev + 1);
            setLatestMessage(msg);
            // Play notification sound & flash title
            playNotificationSound();
            if (document.hidden) {
              flashTitle('💬 New message from support');
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  const markAllRead = useCallback(async () => {
    if (!userId) return;
    try {
      const { data: sessions } = await supabase
        .from('chat_sessions')
        .select('id')
        .eq('user_id', userId)
        .eq('status', 'open');

      if (sessions?.length) {
        const sessionIds = sessions.map(s => s.id);
        await supabase
          .from('chat_messages')
          .update({ read_by_user: true })
          .in('session_id', sessionIds)
          .neq('sender_role', 'user')
          .eq('read_by_user', false);
      }
      setUnreadCount(0);
    } catch (err) {
      console.warn('Failed to mark user chat messages read:', err);
    }
  }, [userId]);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  return { unreadCount, latestMessage, markAllRead, refresh: fetchUnread };
}


export function useAdminChatNotifications() {
  const [unreadCount, setUnreadCount] = useState(0);
  const [latestMessage, setLatestMessage] = useState(null);
  const mountedRef = useRef(true);

  const fetchUnread = useCallback(async () => {
    try {
      // Count all unread user messages across ALL open sessions
      const { count } = await supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .eq('sender_role', 'user')
        .eq('read_by_admin', false);

      if (mountedRef.current) setUnreadCount(count || 0);
    } catch (err) {
      console.warn('Failed to fetch admin chat unread count:', err);
    }
  }, []);

  useEffect(() => { fetchUnread(); }, [fetchUnread]);

  // Real-time subscription – listen for new user messages
  useEffect(() => {
    const channel = supabase
      .channel('admin-chat-notify')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'chat_messages',
        },
        (payload) => {
          const msg = payload.new;
          // Only care about user messages (not admin's own or system)
          if (msg.sender_role !== 'user') return;

          if (mountedRef.current) {
            setUnreadCount(prev => prev + 1);
            setLatestMessage(msg);
            // Play sound & flash title
            playNotificationSound();
            if (document.hidden) {
              flashTitle('💬 New chat message');
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const markRead = useCallback(async (sessionId) => {
    try {
      await supabase
        .from('chat_messages')
        .update({ read_by_admin: true })
        .eq('session_id', sessionId)
        .eq('sender_role', 'user')
        .eq('read_by_admin', false);

      // Refresh total count
      fetchUnread();
    } catch (err) {
      console.warn('Failed to mark admin chat messages read:', err);
    }
  }, [fetchUnread]);

  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  return { unreadCount, latestMessage, markRead, refresh: fetchUnread };
}
